import { StatusId, CommandeMS } from '../types';

// Derivations PURES de l'onglet Notifications et signatures (marche) prestataire-MS. Aucune
// dependance React, aucune I/O. Gating 100% id-based (enum StatusId), jamais par libelle.
//
// Process MS cote presta (signature du MARCHE, apres analyse + notification) :
//   8  EnAttenteSignaturePrestataire / 10 ASignerPrestataire -> c'est au presta de signer
//   11 Valider                                               -> signe des deux cotes (PDF dispo)
//   12 Expirer                                               -> terminal (delai depasse)
//   14 Annule                                                -> terminal (acheteur a annule l'operation)
//   9  EnAttenteSignatureAcheteur                            -> en cours, rien a faire pour le presta
//   13 ANotifier                                             -> statut OPERATION (jamais appose sur une
//                                                               commande presta) ; couvert a titre defensif
// L'onglet n'est atteignable qu'a partir du statut 8 (commandeMsTabs : notifications grisee si <8) ;
// 12 et 14 sont >= 8 donc atteignables -> chacun a son etat terminal explicite (pas de fourre-tout).
// AJOUTS volontaires vs oracle : l'oracle presta n'avait AUCUN bloc pour 9/12/13/14 (section vide ou
// message "non designe" mal place) ; on comble par des etats explicites (attente / expire / annule).

const statusIdOf = (commande: CommandeMS): number | undefined =>
  commande.status?.id ?? commande.id_status;

export interface MarcheState {
  isSigned: boolean; // 11 : signe des deux cotes
  isSignaturePhase: boolean; // 8 | 10 : phase de signature presta
  isExpired: boolean; // 12 : expire (delai depasse)
  isCancelled: boolean; // 14 : annule (acheteur)
  canSign: boolean; // phase signature + le presta connecte EST le signataire designe
  showNotDesignated: boolean; // phase signature + le presta connecte n'est PAS le signataire
  isWaiting: boolean; // 9 (+13 defensif) : en cours cote acheteur, rien a faire pour le presta
}

// Le presta connecte est-il le signataire designe de la commande ?
export const isDesignatedSignataire = (
  commande: CommandeMS,
  userId?: number | string,
): boolean => {
  const sig = commande.prestataire_signataire?.id_user;
  return sig !== undefined && userId !== undefined && String(sig) === String(userId);
};

export const computeMarcheState = (commande: CommandeMS, userId?: number | string): MarcheState => {
  const sid = statusIdOf(commande);
  const isSigned = sid === StatusId.Valider; // 11
  const isExpired = sid === StatusId.Expirer; // 12
  const isCancelled = sid === StatusId.Annule; // 14
  const isSignaturePhase =
    sid === StatusId.EnAttenteSignaturePrestataire || sid === StatusId.ASignerPrestataire; // 8 | 10
  const designated = isDesignatedSignataire(commande, userId);

  return {
    isSigned,
    isSignaturePhase,
    isExpired,
    isCancelled,
    canSign: isSignaturePhase && designated,
    // FIX vs oracle : l'oracle affichait le bouton "Signer" (status 8/10 sans verifier le signataire,
    // Marche.tsx:1395) ET le message "non designe" (l.1412) en meme temps pour un presta non-signataire.
    // Ici on exclut mutuellement : bouton si designe, message sinon.
    showNotDesignated: isSignaturePhase && !designated,
    // Enumeration POSITIVE (9, +13 defensif), PAS un fourre-tout `!autres` : un statut inattendu ne
    // tombe ainsi dans AUCUN etat (section vide) plutot que d'etre avale par un faux "en cours".
    isWaiting: sid === StatusId.EnAttenteSignatureAcheteur || sid === StatusId.ANotifier, // 9, (13 defensif)
  };
};

// Position du prestataire dans l'attribution, une fois que l'acheteur a tranche. La section
// « Information aux candidats » n'affiche QUE la ligne de ce prestataire (les autres candidats ne le
// regardent pas). `null` tant que l'acheteur n'a pas decide (statuts <= 5) ou sur un statut terminal
// non attributif (expire, annule) : la section n'est alors pas rendue.
// Enumeration POSITIVE, comme computeMarcheState : un statut inattendu ne tombe dans aucun cas.
export type PositionCandidat = 'retenu' | 'non_retenu';

export const positionCandidat = (commande: CommandeMS): PositionCandidat | null => {
  const sid = commande.status?.id ?? commande.id_status;
  if (sid === StatusId.Refuser) return 'non_retenu'; // 6
  const retenus: number[] = [
    StatusId.ASigner, // 7
    StatusId.EnAttenteSignaturePrestataire, // 8
    StatusId.EnAttenteSignatureAcheteur, // 9
    StatusId.ASignerPrestataire, // 10
    StatusId.Valider, // 11
    StatusId.ANotifier, // 13
  ];
  return sid !== undefined && retenus.includes(sid) ? 'retenu' : null;
};

// Libelle du signataire designe (pour le message "non designe"). Calque Marche.tsx:1415-1417.
export const designatedSignataireLabel = (commande: CommandeMS): string => {
  const s = commande.prestataire_signataire;
  if (!s) return 'Non défini';
  return `${s.nom ?? ''} ${s.prenom ?? ''} - ${s.email_contact ?? ''}`.trim();
};
